import * as THREE from "three";
import { LookingGlassAdapter } from "./LookingGlassAdapter";
import { VISUAL } from "../config/visualConfig";

export class Diagnostics {
  private frames = 0;
  private lastReport = 0;
  private startedAt = performance.now();
  private lastFrameAt = 0;
  private viewCount = 0;
  private baseline = 0;
  private disparity: number[] = [];
  private error = "";
  private flat = false;
  private readonly details = document.querySelector<HTMLElement>("#diagnostics")!;

  constructor(private readonly renderer: THREE.WebGLRenderer, private readonly status: HTMLElement) {
    window.addEventListener("unhandledrejection", event => {
      if (event.reason instanceof DOMException && event.reason.name === "NotAllowedError"
          && renderer.xr.isPresenting && LookingGlassAdapter.config.lkgCanvas) {
        // Optional screen-placement permission can fail while XR keeps drawing.
        event.preventDefault();
        this.report(performance.now());
        return;
      }
      this.error = event.reason instanceof Error ? event.reason.message : String(event.reason);
      this.report(performance.now());
    });
    renderer.xr.addEventListener("sessionstart", () => {
      this.frames = 0; this.lastFrameAt = 0; this.error = "";
    });
    renderer.xr.addEventListener("sessionend", () => {
      this.frames = 0; this.viewCount = 0; this.baseline = 0;
    });
    const depth = document.querySelector<HTMLInputElement>("#depth-strength")!;
    const flat = document.querySelector<HTMLInputElement>("#flat-test")!;
    const apply = () => {
      this.flat = flat.checked;
      LookingGlassAdapter.config.depthiness = this.flat ? 0 : Number(depth.value);
      document.querySelector("#depth-value")!.textContent = `${Number(depth.value).toFixed(2)}×`;
    };
    depth.addEventListener("input", apply);
    flat.addEventListener("change", apply);
    const blend = document.querySelector<HTMLInputElement>("#blend-views")!;
    blend.addEventListener("change", () => {
      LookingGlassAdapter.config.filterMode = blend.checked ? 1 : 0;
    });
  }

  update(time: number): void {
    if (this.renderer.xr.isPresenting) {
      const cameras = this.renderer.xr.getCamera().cameras;
      this.viewCount = cameras.length;
      const first = cameras[0];
      const last = cameras[cameras.length - 1];
      if (first && last) {
        this.frames += 1;
        this.lastFrameAt = time;
        this.baseline = first.position.distanceTo(last.position);
        // Horizontal image displacement between the first and last rendered view.
        this.disparity = [VISUAL.testDepth, 0, -VISUAL.testDepth].map(z => {
          const point = new THREE.Vector3(0, 0, z);
          return (point.clone().project(last).x - point.project(first).x) / 2 * 100;
        });
      }
    }
    if (time - this.lastReport > 500) this.report(time);
  }

  private report(time: number): void {
    this.lastReport = time;
    const config = LookingGlassAdapter.config;
    const calibrated = Boolean(config.calibration.serial);
    const popup = config.popup;
    const output = config.lkgCanvas;
    const running = this.frames > 0 && time - this.lastFrameAt < 2000;
    if (this.error) this.status.textContent = `XRエラー: ${this.error}`;
    else if (!calibrated) this.status.textContent = time - this.startedAt < 8000
      ? "機器の校正データを取得中…"
      : "機器未検出 — Bridge・USB接続とChromeのローカルネットワーク許可を確認";
    else if (running && (!popup || popup.closed)) this.status.textContent = `${this.viewCount}視点を描画中 / Go出力ウィンドウを開いてください`;
    else if (running && this.viewCount > 1) this.status.textContent = this.flat
      ? `${this.viewCount}視点 / 視差ゼロ比較中`
      : this.baseline > 0.01 ? `${this.viewCount}視点の描画・視差を確認 / 実機の奥行きを確認してください`
        : "多視点ですが視差なし — 奥行き設定を確認";
    else this.status.textContent = `校正取得済み — ${this.renderer.xr.isPresenting ? "描画を待機中" : "EnterでGo画面を開く"}`;

    let placement = "未開始";
    if (popup && !popup.closed && output) {
      const fullscreen = Boolean(popup.document.fullscreenElement);
      const bounds = output.getBoundingClientRect();
      const physicalWidth = Math.round(bounds.width * popup.devicePixelRatio);
      const physicalHeight = Math.round(bounds.height * popup.devicePixelRatio);
      const nativeSize = physicalWidth === output.width && physicalHeight === output.height
        && Math.round(popup.innerWidth * popup.devicePixelRatio) === output.width
        && Math.round(popup.innerHeight * popup.devicePixelRatio) === output.height;
      placement = `${fullscreen ? "全画面" : "ウィンドウ"} / ${popup.innerWidth}×${popup.innerHeight} CSS / DPR ${popup.devicePixelRatio}`;
      placement += ` / 表示 ${physicalWidth}×${physicalHeight}px`;
      if (nativeSize && fullscreen) {
        placement += " / サイズ一致（設置先は実機で確認）";
      } else if (fullscreen) {
        placement += " / サイズ不一致 — Chromeの通知バーを閉じ、Goの解像度・ズームを確認";
        if (running && !this.error) this.status.textContent = "Goの全画面サイズが不一致です — Chromeの通知バーを閉じてください。解像度1440×2560・ズーム100%も確認";
      } else {
        placement += " / Goへ移動してダブルクリック";
      }
    }
    const lines = [
      `校正: ${calibrated ? "取得済み" : "未取得（既定値）"}`,
      `パネル: ${config.calibration.screenW.value}×${config.calibration.screenH.value}`,
      `Quilt: ${config.quiltWidth}×${config.quiltHeight} = ${config.numViews}視点 / ${config.quiltResolution.width}×${config.quiltResolution.height}`,
      `XR: ${running ? "描画中" : "停止"} / 実測 ${this.viewCount}視点 / ${this.frames}フレーム`,
      `カメラ間隔: ${this.baseline.toFixed(3)} / view cone ${config.calibration.viewCone.value}°`,
      `参考点の視差（画幅比）: ${this.disparity.length ? this.disparity.map((d, i) => `${["前", "中央", "奥"][i]} ${d.toFixed(2)}%`).join(" / ") : "未計測"}`,
      `Go出力: ${output ? `${output.width}×${output.height}` : "未作成"}`,
      `奥行き: ${config.depthiness.toFixed(2)}× / 補間: ${config.filterMode === 0 ? "なし（1視点ずつ）" : "あり"}`,
      `配置: ${placement}`,
    ];
    this.details.textContent = lines.join("\n");
  }
}
